import type { UsePromptsReturn } from "../types";
import type { Prompt } from "@webuild-ai/admin/models/components";

import { useState, useCallback, useEffect } from "react";
import { addToast } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

import { useURLState } from "./useURLState";

export const usePrompts = (): UsePromptsReturn => {
  const { adminClient } = useWebuildAPIClient();
  const { t } = useTranslation();
  const { updateURL, initializeFromURL } = useURLState();

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [languageFilter, setLanguageFilter] = useState<string>("");
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize state from URL on mount
  useEffect(() => {
    const urlParams = initializeFromURL();

    setSearchQuery(urlParams.search);
    setLanguageFilter(urlParams.language);
    setIsInitialized(true);
  }, [initializeFromURL]);

  // Update URL when search query or language filter changes, but only after initialization
  useEffect(() => {
    if (isInitialized) {
      updateURL(searchQuery, languageFilter);
    }
  }, [searchQuery, languageFilter, updateURL, isInitialized]);

  const fetchPrompts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminClient?.prompts.listPrompts({
        language: languageFilter || undefined,
      });

      if (response) {
        setPrompts(response);
      }
    } catch (error) {
      logger.error("Error fetching prompts", error);
      addToast({
        title: t("Error"),
        description: t("Failed to load prompts"),
        color: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  }, [adminClient, languageFilter, t]);

  const searchPrompts = useCallback(
    async (searchText: string) => {
      try {
        const response = await adminClient?.prompts.search({
          searchText,
          language: languageFilter || undefined,
        });

        if (!response) {
          logger.error("No response from adminClient?.prompts.search");

          return [];
        }

        return response;
      } catch (error) {
        logger.error("Error searching prompts", error);

        return [];
      }
    },
    [adminClient, languageFilter],
  );

  useEffect(() => {
    const fetchFilteredPrompts = async () => {
      // Don't fetch until URL state is initialized
      if (!isInitialized) {
        return;
      }

      if (!searchQuery.trim()) {
        // If search query is empty, fetch all prompts
        await fetchPrompts();

        return;
      }

      setIsLoading(true);
      try {
        const results = await searchPrompts(searchQuery);

        setPrompts(results);
      } catch (error) {
        logger.error("Error searching prompts", error);
        setPrompts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFilteredPrompts();
  }, [searchQuery, languageFilter, searchPrompts, fetchPrompts, isInitialized]);

  const handleEdit = useCallback((prompt: Prompt) => {
    setEditingPrompt(prompt);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (promptId: string) => {
      try {
        await adminClient?.prompts.delete({ id: promptId });
        // Refresh the current view
        if (!searchQuery.trim()) {
          await fetchPrompts();
        } else {
          const results = await searchPrompts(searchQuery);

          setPrompts(results);
        }
        addToast({
          title: t("Success"),
          description: t("Prompt deleted successfully"),
          color: "success",
        });
      } catch (error) {
        logger.error("Error deleting prompt", error);
        addToast({
          title: t("Error deleting prompt"),
          description:
            error instanceof Error
              ? error.message
              : t("Something went wrong, if the issue persists please contact us"),
          color: "danger",
        });
      }
    },
    [adminClient, searchQuery, fetchPrompts, searchPrompts, t],
  );

  const handleCreate = useCallback(() => {
    setEditingPrompt(null);
    setIsModalOpen(true);
  }, []);

  const refreshPrompts = useCallback(async () => {
    if (!searchQuery.trim()) {
      await fetchPrompts();
    } else {
      const results = await searchPrompts(searchQuery);

      setPrompts(results);
    }
  }, [searchQuery, fetchPrompts, searchPrompts]);

  return {
    prompts,
    isLoading,
    searchQuery,
    setSearchQuery,
    languageFilter,
    setLanguageFilter,
    handleEdit,
    handleDelete,
    handleCreate,
    refreshPrompts,
    editingPrompt,
    isModalOpen,
    setIsModalOpen,
  };
};
